import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Instagram, Globe, MapPin, Clock, ChevronRight, Award, Flame } from "lucide-react";
import Navbar, { Logo } from "../components/Navbar";
import Footer from "../components/Footer";
import donerAbout from "../assets/doner_about.jpg";
import donerMenu from "../assets/doner_menu.jpg";
import pizzaMenu from "../assets/pizza_menu.jpg";
import salatMenu from "../assets/salat_menu.jpg";
import vegiMenu from "../assets/vegi_menu.jpg";

const Home = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-testid="hero-section">
        <div className="absolute inset-0 bg-grain">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url(${donerAbout})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.35) contrast(1.1)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl fade-in">
          <div className="inline-block px-4 py-1 border border-red-600/50 bg-red-600/10 uppercase tracking-[0.3em] text-xs text-red-400 mb-8">
            Stuttgart · Seit Jahren
          </div>
          <div className="flex justify-center mb-10">
            <Logo size="lg" />
          </div>
          <h1 className="display text-5xl md:text-8xl font-bold mb-6 leading-none">
            <span className="block text-white">TÜRKISCHE</span>
            <span className="block text-red-600">SPEZIALITÄTEN</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10">
            Gutes Essen · Erlesene Getränke · Leidenschaftliche Küche
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/speisekarte"
              className="group px-8 py-4 bg-red-600 hover:bg-red-700 uppercase tracking-widest font-bold flex items-center justify-center gap-2 transition-all"
              data-testid="hero-menu-btn"
            >
              Speisekarte ansehen
              <ChevronRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
            <a
              href="tel:+4971175486846"
              className="px-8 py-4 border-2 border-white/30 hover:border-red-600 hover:bg-red-600/10 uppercase tracking-widest font-bold flex items-center justify-center gap-2 transition-all"
              data-testid="hero-call-btn"
            >
              <Phone size={18}/>0711 75486846
            </a>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-xs uppercase tracking-widest animate-pulse">
          Scroll ↓
        </div>
      </section>

      {/* ÜBER UNS */}
      <section id="about" className="py-24 px-6 bg-neutral-950 relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="handwritten text-red-500 text-xl mb-3">— Über Uns</div>
            <h2 className="display text-5xl md:text-6xl font-bold mb-2">TRADITION</h2>
            <h2 className="display text-5xl md:text-6xl font-bold text-red-600 mb-6">UND MODERNE</h2>
            <div className="w-20 h-1 bg-red-600 mb-8"></div>
            <h3 className="text-2xl mb-4 text-white/90">Gastfreundschaft und Qualität</h3>
            <p className="text-white/70 leading-relaxed mb-8">
              Unser Restaurant vereint traditionelle türkische Aromen mit moderner Präsentation. Unsere Priorität liegt
              auf Geschmack und Qualität, und wir sind stolz darauf, unseren Gästen ein unvergessliches kulinarisches
              Erlebnis zu bieten. Wir freuen uns darauf, Sie in unserem Restaurant begrüßen zu dürfen!
            </p>
            <div className="flex gap-4 flex-wrap">
              <div className="flex items-center gap-3 px-4 py-3 border border-red-600/30 bg-red-600/5">
                <Award className="text-red-500" size={28}/>
                <div>
                  <div className="display text-white text-lg leading-none">Seit Jahren</div>
                  <div className="text-xs text-white/50 uppercase tracking-widest">Beste Qualität</div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 border border-red-600/30 bg-red-600/5">
                <Flame className="text-red-500 flame" size={28}/>
                <div>
                  <div className="display text-white text-lg leading-none">Frisch</div>
                  <div className="text-xs text-white/50 uppercase tracking-widest">Jeden Tag</div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative group" data-testid="about-image">
            <div className="absolute -inset-4 bg-red-600/20 blur-2xl group-hover:bg-red-600/30 transition-colors"></div>
            <div className="relative overflow-hidden border-2 border-red-600/50">
              <img src={donerAbout} alt="Döner Kebap" className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"/>
            </div>
            <div className="absolute -top-5 -right-5 bg-red-600 text-white px-5 py-4 display text-2xl -rotate-6 shadow-2xl">
              #1 Stuttgart
            </div>
          </div>
        </div>
      </section>

      {/* ÖFFNUNGSZEITEN */}
      <section id="hours" className="py-24 px-6 bg-black relative overflow-hidden" data-testid="hours-section">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent"></div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-64 h-64 border-4 border-red-600 rounded-full flex items-center justify-center relative">
                <Clock className="text-red-600" size={120} strokeWidth={1.2}/>
                <div className="absolute -top-3 -right-3 bg-red-600 text-white px-4 py-1 uppercase tracking-widest text-sm display">Open</div>
              </div>
            </div>
          </div>
          <div>
            <div className="handwritten text-red-500 text-xl mb-3">— Wir haben für Sie geöffnet</div>
            <h2 className="display text-5xl md:text-6xl font-bold mb-8">ÖFFNUNGSZEITEN</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-red-600/30 pb-3" data-testid="hours-weekdays">
                <span className="display text-2xl">Montag – Freitag</span>
                <span className="text-red-500 display text-2xl">11:00 – 22:00</span>
              </div>
              <div className="flex justify-between items-center border-b border-red-600/30 pb-3" data-testid="hours-weekend">
                <span className="display text-2xl">Samstag – Sonntag</span>
                <span className="text-red-500 display text-2xl">12:00 – 22:00</span>
              </div>
              <div className="flex justify-between items-center pb-3">
                <span className="display text-2xl text-white/70">Feiertage</span>
                <span className="text-red-500 display text-2xl">12:00 – 22:00</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPEISEKARTE */}
      <section id="menu" className="py-24 px-6 bg-neutral-950" data-testid="menu-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="handwritten text-red-500 text-xl mb-3">— Qualitätszutaten, köstliche Mahlzeiten</div>
            <h2 className="display text-5xl md:text-6xl font-bold">UNSERE <span className="text-red-600">SPEISEKARTE</span></h2>
            <p className="text-white/60 max-w-2xl mx-auto mt-6">
              Unsere Gerichte werden aus sorgfältig ausgewählten, hochwertigen Zutaten zubereitet — jeder Bissen unvergesslich.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { img: donerMenu, title: "Döner Kebap", price: "ab 8,00 €", desc: "Mit Brot, Yufka oder Box." },
              { img: pizzaMenu, title: "Pizza", price: "ab 8,00 €", desc: "Köstliche hausgemachte Pizzen." },
              { img: salatMenu, title: "Salat", price: "ab 5,90 €", desc: "Frisch & knackig zubereitet." },
              { img: vegiMenu, title: "Vegetarisch", price: "ab 7,50 €", desc: "Falafel, Halloumi & mehr." },
            ].map((it) => (
              <Link
                to="/speisekarte"
                key={it.title}
                className="group relative overflow-hidden bg-black border border-red-600/20 hover:border-red-600 transition-all"
                data-testid={`menu-card-${it.title.toLowerCase().replace(/\s+/g, '-').replace(/ö/g, 'o')}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img src={it.img} alt={it.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"/>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="display text-2xl mb-1">{it.title}</h3>
                  <p className="text-white/60 text-sm mb-2">{it.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-red-500 display text-xl">{it.price}</span>
                    <span className="text-white/40 text-xs uppercase tracking-widest group-hover:text-red-500 transition-colors flex items-center gap-1">
                      Mehr <ChevronRight size={14}/>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/speisekarte"
              className="inline-flex items-center gap-2 px-10 py-4 bg-red-600 hover:bg-red-700 uppercase tracking-widest font-bold transition-all"
              data-testid="menu-fullmenu-btn"
            >
              Komplette Speisekarte <ChevronRight size={18}/>
            </Link>
          </div>
        </div>
      </section>

      {/* KONTAKT */}
      <section id="contact" className="py-24 px-6 bg-black" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="handwritten text-red-500 text-xl mb-3">— Besuchen Sie uns</div>
            <h2 className="display text-5xl md:text-6xl font-bold">KONTAKT <span className="text-red-600">& ADRESSE</span></h2>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            <div className="space-y-6">
              <div className="p-6 border border-red-600/30 bg-red-600/5 hover:border-red-600 transition-colors">
                <div className="flex items-start gap-4">
                  <MapPin className="text-red-500 mt-1" size={24}/>
                  <div>
                    <h4 className="display text-xl text-white mb-1">Adresse</h4>
                    <p className="text-white/70">Schloßstraße 77, 70176 Stuttgart</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <a href="tel:+4971175486846" className="p-5 border border-red-600/30 bg-red-600/5 hover:bg-red-600/20 transition-colors" data-testid="contact-phone">
                  <Phone className="text-red-500 mb-2" size={22}/>
                  <div className="text-xs text-white/50 uppercase tracking-widest mb-1">Telefon</div>
                  <div className="display text-lg">0711 75486846</div>
                </a>
                <a href="https://wa.me/4916321479581" target="_blank" rel="noreferrer" className="p-5 border border-red-600/30 bg-red-600/5 hover:bg-red-600/20 transition-colors" data-testid="contact-whatsapp">
                  <MessageCircle className="text-red-500 mb-2" size={22}/>
                  <div className="text-xs text-white/50 uppercase tracking-widest mb-1">WhatsApp</div>
                  <div className="display text-lg">0163 2147958</div>
                </a>
                <a href="https://www.bydoner.de" target="_blank" rel="noreferrer" className="p-5 border border-red-600/30 bg-red-600/5 hover:bg-red-600/20 transition-colors" data-testid="contact-web">
                  <Globe className="text-red-500 mb-2" size={22}/>
                  <div className="text-xs text-white/50 uppercase tracking-widest mb-1">Web</div>
                  <div className="display text-lg">bydoner.de</div>
                </a>
                <a href="https://instagram.com/bydoner_stuttgart" target="_blank" rel="noreferrer" className="p-5 border border-red-600/30 bg-red-600/5 hover:bg-red-600/20 transition-colors" data-testid="contact-instagram">
                  <Instagram className="text-red-500 mb-2" size={22}/>
                  <div className="text-xs text-white/50 uppercase tracking-widest mb-1">Instagram</div>
                  <div className="display text-lg">@bydoner_stuttgart</div>
                </a>
              </div>
            </div>

            <div className="overflow-hidden border-2 border-red-600/30 min-h-[400px]" data-testid="contact-map">
              <iframe
                title="BY Döner Stuttgart Karte"
                src="https://www.google.com/maps?q=Schlo%C3%9Fstra%C3%9Fe+77+70176+Stuttgart&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 400, filter: "grayscale(0.3) contrast(1.1)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
